import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import Text, { BoldText } from '../../components/Text'
import TextArea from '../../components/TextArea'
import seasonStore from '../../stores/seasonStore'
import { colors, fontSize, spacing } from '../../styles/_var'
import { Table, TableWrapper, TextWithIcon } from '../VoterDashboard/components/VoterDashboardStyled'
import { CompetitionPageWrapper } from './StyledComponents'
import EmptyResource from '../../components/EmptyResource'
import { observer } from 'mobx-react-lite'
import { beautifyDate, extractEmails } from '../../utils/helpers'
import uiStore from '../../stores/uiStore'
import eligibleVoterStore from '../../stores/eligibleVoterStore'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../utils/constants'
import { ErrorMessage } from '../../components/StyledComponents'
import usePageTitle from '../../hooks/usePageTitle'
import AllowedVotersForm, { IAllowedVotersFormPayload } from './components/AllowedVotersForm'
import { SaveAllowedVotersPayload } from '../../utils/interfaces'
import { Note } from './CompetitionAccount'
import competitionDashboardStore from '../../stores/competitionDashboardStore'

const AllowedVoters = () => {
  const {
    eligibleVoters,
    pendingInvitations,
    hasEligibleVoters,
    hasPendingInvitations,
    invitationPagination,
    shouldShowEmptyState,
    votersPagination
  } = eligibleVoterStore
  const navigate = useNavigate()
  const { has_next: inviteHasNext } = invitationPagination
  const { has_next: votersHasNext } = votersPagination
  const [canShowAddModal, setShowAddModal] = useState(false)
  const seasonId = seasonStore.selectedSeasonId
  usePageTitle('Eligible Voters')

  useEffect(() => {
    if (competitionDashboardStore.isCompetitionPublic) {
      navigate(routes.FORBIDDEN)
    } else {
      eligibleVoterStore.fetchEligibleVoters(seasonId, 1)
      eligibleVoterStore.fetchPendingInvitations(seasonId, 1) 
    }
  }, [seasonId, navigate])

  const toggleModal = () => setShowAddModal(!canShowAddModal)

  const onRevokeInvitation = (email: string) => () => {
    uiStore.openConfirmDialog("Are you sure you want to revoke this invitation", () => {
      eligibleVoterStore.revokeInvitation(seasonId, email)
    })
  }

  const onRemoveEligibleVoter = (email: string) => () => {
    uiStore.openConfirmDialog("Are you sure you want to remove this voter", () => {
      eligibleVoterStore.removeEligibleVoter(seasonId, email)
    })
  }

  const addVoters = async (formPayload: IAllowedVotersFormPayload) => {
    const validVoters = extractEmails(formPayload.voter_emails || '')
    const payload: SaveAllowedVotersPayload = {...formPayload, voter_emails: validVoters}
    await eligibleVoterStore.saveEligibleVoters(seasonId, payload)
    toggleModal()
  }

  const renderAllowedVoters = () => {
    return (
      <div role="region" aria-label="allowed voters section">
        <Text variant="medium">Users</Text>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <td><Text variant="regular">First Name</Text></td>
                <td><Text variant="regular">Last Name</Text></td>
                <td><Text variant="regular">Email</Text></td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {eligibleVoters.map((voter, i) => (
                <tr key={i} role="listitem" aria-label={voter.email}>
                  <td><Text variant="subtext">{voter.first_name}</Text></td>
                  <td><Text variant="subtext">{voter.last_name}</Text></td>
                  <td><Text variant="subtext">{voter.email}</Text></td>
                  <td>
                    <DeleteLink
                      variant="subtext"
                      onClick={onRemoveEligibleVoter(voter.email)}
                      role="button">
                      delete
                    </DeleteLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        {votersHasNext && (
          <Button
            alignCenter
            variant='secondary'
            mt={1}
            onClick={() => eligibleVoterStore.fetchEligibleVoters(seasonId)}
          >
            Load More
          </Button>
        )}
      </div>
    )
  }

  const renderPendingInvitations = () => {
    return (
      <PendingInvitationSection role="region" aria-label="pending invitations section">
        <Text variant="medium">Pending Invitations</Text>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <td><Text variant="regular">Email</Text></td>
                <td><Text variant="regular">Date sent</Text></td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {pendingInvitations.map((invite, i) => (
                <tr key={i} role="listitem" aria-label={invite.email}>
                  <td><Text variant="subtext">{invite.email}</Text></td>
                  <td><Text variant="subtext">{beautifyDate(invite.created_at)}</Text></td>
                  <td>
                    <DeleteLink
                      variant="subtext"
                      onClick={onRevokeInvitation(invite.email)}
                      role="button"
                    >
                      delete
                    </DeleteLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        {inviteHasNext && (
          <Button
            variant='secondary'
            alignCenter
            loading={eligibleVoterStore.fetchingAllowedVoters}
            mt={1}
            onClick={() => eligibleVoterStore.fetchPendingInvitations(seasonId)}>
            Load More
          </Button>
        )}
      </PendingInvitationSection>
    )
  }

  const hasDomain = !!competitionDashboardStore.data.competitionDetails.allowed_email_domain

  return (
    <CompetitionPageWrapper>
      <HeaderSection>
        <Text variant="large" mb={2}>ELIGIBLE VOTERS</Text>
        <StyledTextWithIcon
          onClick={toggleModal}
          role="button"
          aria-label="add-voter-button"
        >
          <FontAwesomeIcon icon={faPlusCircle} size="2x" />
          <BoldText variant="regular" ml={1} id="add-voter-button">
            ADD ELIGIBLE VOTERS
          </BoldText>
        </StyledTextWithIcon>
      </HeaderSection>
      {hasDomain && (
        <Note>
          <Text variant="medium">
            All registered users with email ending in {" "}
            <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
              {competitionDashboardStore.data.competitionDetails.allowed_email_domain}
            </span>
            {" "}will be granted access to vote in this private campaign.
          </Text>
          <Button variant="danger" onClick={() => toggleModal()} alignCenter mt={2}>
            Remove Allowed Domain
          </Button>
        </Note>
      )}
      {shouldShowEmptyState && (
        <EmptyResource
          createResourceAction={() => toggleModal()}
          createResourceButtonText="Add Eligible Voters"
          createResourceText="You haven't added any voters to this competition" />
      )}
      {hasEligibleVoters && renderAllowedVoters()}
      {hasPendingInvitations && !hasDomain && renderPendingInvitations()}
      <Modal showModal={canShowAddModal} onClose={toggleModal}>
        <AllowedVotersForm addVoters={addVoters} />
      </Modal>
    </CompetitionPageWrapper>
  )
}

export default observer(AllowedVoters)

export const ValidationErrorMessage = styled(({ ...rest }) => <ErrorMessage {...rest} />)`
  position: relative;
  box-sizing: border-box;
  color: ${colors.red};
`

export const EligibleVotersInput = styled(({ ...rest }) => <TextArea {...rest} />)`
  font-size: ${fontSize.sm};
  line-height: 1.9rem;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.sm};
`

const StyledTextWithIcon = styled(({ ...rest }) => <TextWithIcon {...rest} />)`
  color: ${colors.purple};
  cursor: pointer;
  transition: all ease .2s;
  height: 2rem;

  &:hover {
    opacity: .8;
    text-shadow: 1px 1px ${colors.lightgrey};
  }
`

const DeleteLink = styled(({ ...rest }) => <Text {...rest} />)`
  cursor: pointer;
  color: ${colors.purple};

  &:hover {
    text-decoration: underline;
  }
`

const PendingInvitationSection = styled.div`
  margin-top: ${spacing.sm};
`
